import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { istDateKey } from '@/lib/ist'
import { useSyncStatus } from '@/offline'
import { useToast } from '@/ui'
import type { DraftRecord } from '@/offline'
import type { MessageKey } from '@/i18n'
import type { VoidDeliveryReason } from '../commands'
import {
  closeDay,
  deleteOpenDelivery,
  openDay,
  scrubSyncedBank,
  scrubSyncedMoney,
} from '../data/actions'
import type { GateSession } from '../data/session'
import { useGate } from '../data/useGate'
import { bagWeight, v1TasksLeft, type DeliveryDraft } from '../model/delivery'
import { deliveriesOf, reconcile, type DayDelivery } from '../model/day'
import { hasNoDeductions, inForce, anyUsableMainCard } from '../model/rateCards'
import { TASK_SHORT } from './delivery/taskNames'
import {
  Avatar,
  Btn,
  Card,
  Cta,
  Footer,
  Icon,
  ItemRow,
  JobTile,
  Keypad,
  SheetActions,
  Strip,
  Tick,
  V1Header,
  V1Screen,
  V1Sheet,
  WaitCta,
} from './kit'
import { C, typeKey } from './style'
import type { V1IconName } from './sprite'
import { cropName, tr, fmtDate, fmtTime, fmtWhen, kg, kgWhole, price, rosterShort } from './text'
import { Rich } from './Rich'
import { retagWaiting } from './retagWaiting'
import { rosterNoMatches } from '../model/search'

function usePending() {
  const sync = useSyncStatus()
  return sync
}

/** Battery and free space for the chip under "दिन खुला" (only what the phone tells the page). */
function usePhoneHealth() {
  const [pct, setPct] = useState<number | null>(null)
  const [spaceLow, setSpaceLow] = useState<boolean | null>(null)
  useEffect(() => {
    let alive = true
    const nav = navigator as Navigator & { getBattery?: () => Promise<{ level: number }> }
    void nav.getBattery?.().then((b) => alive && setPct(Math.round(b.level * 100)))
    void navigator.storage?.estimate?.().then((e) => {
      if (!alive || e.quota == null || e.usage == null) return
      setSpaceLow(e.quota - e.usage < 200 * 1024 * 1024)
    })
    return () => {
      alive = false
    }
  }, [])
  return { pct, spaceLow }
}

/** F4.1 — आज: the day, its numbers, the farmers open now (newest on top) and the four jobs. */
export function V1Home() {
  const navigate = useNavigate()
  const g = useGate()
  const sync = usePending()
  const health = usePhoneHealth()
  const [now] = useState(() => Date.now())
  const s = g.app
  const rec = useMemo(() => (s ? reconcile(g.outbox, s.id) : null), [g.outbox, s])
  const deliveries = useMemo(() => (s ? deliveriesOf(g.outbox, s.id) : []), [g.outbox, s])
  // F4.6-h: bags and samples the office kept without a tag (the supervisor's नया टैग).
  const actor = useActor()
  const supervisor = hasAnyRole(actor.roles, [ROLES.SUPERVISOR])
  const toRetag = useMemo(() => retagWaiting(g.outbox).length, [g.outbox])
  const [deleting, setDeleting] = useState<DraftRecord<DeliveryDraft> | null>(null)

  useEffect(() => {
    void scrubSyncedMoney()
    void scrubSyncedBank()
  }, [sync.pending])

  if (!g.ready || !g.roster) return <V1Screen />
  // Several farmers can be open at once: whoever is slow waits while she serves the next.
  const open = g.drafts
    .filter((x) => x.data.capture_mode === 'APP' && s && x.data.session_id === s.id)
    .sort((a, b) => b.created_at - a.created_at)
  const farmers = new Set(deliveries.filter((x) => x.kind === 'COMMITTED').map((x) => x.farmer_id))
    .size
  const bags = rec ? rec.bagsMain + rec.bagsConv : 0
  const net = rec ? rec.netMainG + rec.netConvG : 0
  const lots = rec ? rec.lotsMain + rec.lotsConv : 0
  const chip =
    health.pct !== null
      ? tr(health.spaceLow ? 'gate.v1.home.batteryLow' : 'gate.v1.home.battery', {
          pct: health.pct,
        })
      : health.spaceLow !== null
        ? tr(health.spaceLow ? 'gate.v1.home.spaceLow' : 'gate.v1.home.spaceOk')
        : null
  const chipBad = (health.pct !== null && health.pct < 20) || health.spaceLow === true

  return (
    <V1Screen
      nav
      main={{ gap: 8 }}
      header={
        <V1Header
          pending={sync.pending}
          updatable
          icon="home"
          title={tr('gate.v1.home.title', { date: fmtDate(now) })}
        />
      }
      footer={
        <Footer>
          {!s ? (
            <Cta
              label={tr('gate.v1.home.openDay')}
              icon="shutterup"
              onClick={() => navigate('/m/gate/open-day')}
            />
          ) : (
            <Cta
              label={tr('gate.v1.home.nextFarmer')}
              icon="user"
              lead
              onClick={() => navigate('/m/gate/delivery/new')}
            />
          )}
        </Footer>
      }
    >
      {/* The day, its numbers and the jobs stay put; only the open farmers below scroll. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        <ItemRow
          padding="8px 10px"
          lead={
            <span
              aria-hidden="true"
              style={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: 10,
                background: s ? C.okWash : C.sunk,
                color: s ? C.ok : C.ink,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={s ? 'shutterup' : 'shutterdown'} size={30} />
            </span>
          }
          titleSize={18}
          title={tr(s ? 'gate.v1.home.dayOpen' : 'gate.v1.home.dayClosed')}
          line={
            s ? (
              <span style={{ color: C.muted }}>
                {tr('gate.v1.home.dayOpenLine', {
                  village: s.village_name,
                  time: fmtTime(s.opened_at),
                })}
              </span>
            ) : undefined
          }
          right={s ? <Tick size={28} /> : undefined}
        />
        {chip && (
          <div style={{ display: 'flex' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 10px 3px 6px',
                borderRadius: 16,
                background: chipBad ? C.warnWash : C.okWash,
                color: chipBad ? C.accDark : C.ok,
                fontSize: 15,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              <Icon name="phone" size={20} />
              {chip}
            </span>
          </div>
        )}
        {s && (
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}
          >
            {(
              [
                [String(farmers), tr('gate.v1.home.farmers')],
                [String(bags), tr('gate.v1.home.bags')],
                [kgWhole(net), tr('gate.v1.home.kg')],
              ] as const
            ).map(([v, k]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '4px 2px',
                  borderRadius: 10,
                  background: C.surface,
                  border: `1.5px solid ${C.rule}`,
                }}
              >
                <b style={{ fontSize: 22, lineHeight: 1.15 }}>{v}</b>
                <span style={{ fontSize: 14, color: C.muted }}>{k}</span>
              </div>
            ))}
          </div>
        )}
        {!supervisor && toRetag > 0 && (
          // Only the supervisor re-tags; the operator just learns that someone must be called.
          <Strip icon="tag">{tr('gate.v1.retag.waitNotice', { count: toRetag })}</Strip>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          <JobTile
            icon="truck"
            color={C.acc}
            title={tr('gate.v1.home.load')}
            line={s ? tr('gate.v1.home.loadLine', { lots, bags }) : undefined}
            onClick={() => navigate('/m/store/load')}
          />
          <JobTile
            icon="passbook"
            color={C.ok}
            title={tr('gate.v1.home.pay')}
            onClick={() => navigate('/m/gate/deliveries')}
          />
          {/* Paper back-entry stays at /m/gate/paper (office); the tile is now the bank account. */}
          <JobTile
            icon="passbook"
            color={C.accDark}
            title={tr('gate.v1.home.collectBank')}
            line={tr('gate.v1.home.collectBankLine')}
            onClick={() => navigate('/m/gate/bank')}
          />
          {s && (
            // Only an open day can be closed (the close screen sends a closed day back here).
            <JobTile
              icon="shutterdown"
              color={C.ink}
              title={tr('gate.v1.home.closeDay')}
              onClick={() => navigate('/m/gate/close-day')}
            />
          )}
          {supervisor && (
            <JobTile
              icon="tag"
              color={toRetag ? C.bad : C.ink}
              title={tr('gate.v1.retag.tile')}
              line={toRetag ? tr('gate.v1.retag.tileLine', { count: toRetag }) : undefined}
              onClick={() => navigate('/m/gate/retag')}
            />
          )}
        </div>
      </div>
      {open.length > 0 && (
        <div
          style={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {open.map((x) => (
            <OpenFarmerCard
              key={x.id}
              d={x.data}
              onOpen={() =>
                navigate(`/m/gate/delivery/new?id=${encodeURIComponent(x.data.delivery_ulid)}`)
              }
              onDelete={() => setDeleting(x)}
            />
          ))}
        </div>
      )}
      {deleting && s && (
        <DeleteFarmerSheet
          d={deleting.data}
          session={s}
          actorId={actor.user_id}
          onClose={() => setDeleting(null)}
        />
      )}
    </V1Screen>
  )
}

/** One farmer open on Home: his photo, name, what is left to do, and a delete icon. */
function OpenFarmerCard({
  d,
  onOpen,
  onDelete,
}: {
  d: DeliveryDraft
  onOpen: () => void
  onDelete: () => void
}) {
  // Only what is left: the crop, then the tasks still open, then the slip and the final check.
  const left = d.crop_id ? v1TasksLeft(d) : null
  const ready = left !== null && left.length === 0
  const status =
    d.slip.farmer_agreed_at !== null || d.step === 'close'
      ? tr('gate.v1.final.title')
      : left === null
        ? tr('gate.v1.home.left', { tasks: tr('gate.v1.crop.title') })
        : ready
          ? tr('gate.v1.home.readySlip')
          : tr('gate.v1.home.left', { tasks: left.map((t) => tr(TASK_SHORT[t])).join(', ') })
  // One card: the farmer (tap to carry on) and, behind a thin rule, the delete icon. Two buttons
  // side by side, since a button cannot hold another.
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
        borderRadius: 12,
        border: `2px solid ${C.acc}`,
        background: C.surface,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        style={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '6px 4px 6px 10px',
          border: 'none',
          background: 'transparent',
          color: C.ink,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <Avatar url={d.farmer.photo_url} size={44} icon={37} />
        <span
          style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <b
            style={{
              fontSize: 18,
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {d.farmer.name}
          </b>
          <span
            style={{
              fontSize: 15,
              color: ready ? C.ok : C.muted,
              fontWeight: ready ? 700 : 400,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {status}
          </span>
        </span>
        <span style={{ color: C.acc, display: 'flex', flexShrink: 0 }}>
          <Icon name="next" size={24} sw={2.4} />
        </span>
      </button>
      <span aria-hidden="true" style={{ width: 1, margin: '10px 0', background: C.rule }} />
      <button
        type="button"
        aria-label={tr('gate.v1.home.deleteFarmer', { name: d.farmer.name })}
        onClick={onDelete}
        style={{
          flex: '0 0 auto',
          width: 48,
          border: 'none',
          background: 'transparent',
          color: C.bad,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <Icon name="trash" size={22} />
      </button>
    </div>
  )
}

const DELETE_REASONS: [VoidDeliveryReason, MessageKey][] = [
  ['WRONG_FARMER', 'gate.v1.delete.wrongFarmer'],
  ['DUPLICATE', 'gate.v1.delete.duplicate'],
  ['WALK_AWAY', 'gate.v1.cancel.left'],
]

/** Delete an open farmer? His name, photo and what was done; a reason; tags and seal voided. */
function DeleteFarmerSheet({
  d,
  session,
  actorId,
  onClose,
}: {
  d: DeliveryDraft
  session: GateSession
  actorId: string
  onClose: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState<VoidDeliveryReason | null>(null)
  const [busy, setBusy] = useState(false)
  const live = d.bags.filter((b) => !b.withdrawn)
  const grossG = live.reduce((sum, b) => sum + (bagWeight(b)?.gross_g ?? 0), 0)
  const used = live.length > 0 || d.sample !== null

  const remove = async () => {
    if (!reason) return
    setBusy(true)
    try {
      await deleteOpenDelivery(d, reason, session, actorId)
      onClose()
    } catch (e) {
      toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <V1Sheet
      title={tr('gate.v1.delete.title', { name: d.farmer.name })}
      onClose={onClose}
      top={160}
      actions={
        reason ? (
          <SheetActions>
            <Btn icon="back" onClick={onClose}>
              {tr('gate.v1.back')}
            </Btn>
            <Cta
              label={tr('gate.v1.delete.confirm')}
              icon="trash"
              busy={busy}
              onClick={() => void remove()}
            />
          </SheetActions>
        ) : (
          <SheetActions>
            <Btn icon="back" onClick={onClose}>
              {tr('gate.v1.back')}
            </Btn>
            <WaitCta>{tr('gate.v1.pickReason')}</WaitCta>
          </SheetActions>
        )
      }
    >
      <ItemRow
        padding="8px 12px"
        lead={<Avatar url={d.farmer.photo_url} size={48} icon={40} />}
        titleSize={18}
        title={d.farmer.name}
        line={tr('gate.v1.farmerNo', { no: rosterShort(d.farmer.roster_no) })}
      />
      {live.length > 0 && (
        <ItemRow
          padding="8px 12px"
          lead={<Icon name="sack" size={28} />}
          title={`${tr('gate.v1.bags', { count: live.length })} · ${tr('gate.v1.kg', { kg: kg(grossG) })}`}
        />
      )}
      {used && <Strip icon="scissors">{tr('gate.v1.delete.voidCodes')}</Strip>}
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
        {tr('gate.v1.why')}
      </h2>
      <div style={{ display: 'grid', gap: 8 }}>
        {DELETE_REASONS.map(([r, key]) => (
          <button
            key={r}
            type="button"
            aria-pressed={reason === r}
            onClick={() => setReason(r)}
            style={{
              minHeight: 52,
              borderRadius: 12,
              border: reason === r ? `3px solid ${C.ink}` : `1.5px solid ${C.border}`,
              background: C.surface,
              color: C.ink,
              padding: '6px 10px',
              fontFamily: "'Mukta', sans-serif",
              fontSize: 18,
              fontWeight: 700,
              width: '100%',
              cursor: 'pointer',
            }}
          >
            {tr(key)}
          </button>
        ))}
      </div>
    </V1Sheet>
  )
}

/** F4.1 — दिन शुरू करें: set up, check the list and the price, open. No scale test, no roll scans. */
export function V1OpenDay() {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const g = useGate()
  const sync = usePending()
  const [now] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)
  if (!g.ready || !g.roster) return <V1Screen />
  if (g.app) return <Navigate to="/m/gate" replace />
  const r = g.roster
  const reg = g.info.registration
  const today = istDateKey(now)
  const issued = (kind: 'BAG' | 'SEAL') =>
    r.rolls.some((x) => x.kind === kind && x.issued !== false)
  const stale = g.info.freshness === 'expired' || g.info.freshness === 'missing'
  const crops = [...new Set(r.rateCards.filter((c) => inForce(c, today)).map((c) => c.crop_id))]
  const problem = stale
    ? tr('gate.v1.open.listOld')
    : !anyUsableMainCard(r.rateCards, today)
      ? tr('gate.v1.open.noCard')
      : !issued('BAG') || !issued('SEAL')
        ? tr('gate.v1.open.noRolls')
        : null

  const submit = async () => {
    if (problem || !reg) return
    setBusy(true)
    try {
      await openDay({
        actorId: actor.user_id,
        rosterVersion: g.info.rosterVersion,
        rolls: [],
        session: {
          capture_mode: 'APP',
          group_id: reg.team.id,
          village_name: reg.team.name,
          operator_id: actor.user_id,
          operator_name: actor.name,
          opened_at: new Date().toISOString(),
          scale_equipment_id: null,
          scale_label: null,
          stamp_ok: null,
          test_weight_g: null,
          test_weight_read_g: null,
          tags_in_hand: 0,
          seals_in_hand: 0,
          paper: null,
        },
      })
      navigate('/m/gate', { replace: true })
    } catch (e) {
      toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const setup: [V1IconName, string][] = [
    ['scale', tr('gate.v1.open.scale')],
    ['rupee', tr('gate.v1.open.rateChart')],
    ['redtie', tr('gate.v1.open.redLine')],
    ['roster', tr('gate.v1.open.roster')],
  ]

  return (
    <V1Screen
      nav
      header={
        <V1Header
          onBack={() => navigate('/m/gate')}
          pending={sync.pending}
          icon="shutterup"
          title={tr('gate.v1.open.title')}
        />
      }
      footer={
        <Footer>
          {problem ? (
            <WaitCta>{tr('gate.v1.open.title')}</WaitCta>
          ) : (
            <Cta
              label={tr('gate.v1.open.cta')}
              icon="shutterup"
              busy={busy}
              onClick={() => void submit()}
            />
          )}
        </Footer>
      }
    >
      <Card gap={6}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25 }}>
            {reg?.team.name ?? '—'}
          </span>
          <span style={{ fontSize: 17, lineHeight: 1.3 }}>{fmtDate(now)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="roster" size={22} style={{ color: C.acc }} />
          <span style={{ fontSize: 17, lineHeight: 1.3 }}>
            {tr('gate.v1.open.listFrom', {
              when: r.downloadedAt ? fmtWhen(r.downloadedAt, now) : '—',
            })}
          </span>
          <span style={{ flexGrow: 1 }} />
          <Tick ok={!stale} size={26} />
        </div>
      </Card>
      {problem && <Strip icon="bang">{problem}</Strip>}
      <Card gap={8}>
        <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25 }}>
          {tr('gate.v1.open.setupFirst')}
        </span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
          {setup.map(([icon, label]) => (
            <div
              key={label}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: 12,
                  background: C.sunk,
                  color: C.acc,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name={icon} size={28} />
              </span>
              <span
                style={{ fontSize: 15, fontWeight: 600, textAlign: 'center', lineHeight: 1.15 }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </Card>
      {crops.map((crop) => (
        <Card key={crop} gap={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="rupee" size={22} style={{ color: C.acc }} />
            <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25 }}>
              {tr('gate.v1.open.rates', { crop: cropName(r.config.crops, crop) })}
            </span>
          </div>
          {(['MAIN', 'CONV'] as const).map((stream) => {
            const c = r.rateCards.find(
              (x) => x.crop_id === crop && x.stream === stream && inForce(x, today),
            )
            if (!c) return null
            const ok = hasNoDeductions(c.deduction_rules) && c.price_paise_per_kg !== null
            return (
              <div key={stream} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon
                  name={stream === 'MAIN' ? 'sack' : 'redtie'}
                  size={24}
                  style={{ color: stream === 'MAIN' ? C.ok : C.ink }}
                />
                <span style={{ fontSize: 17, lineHeight: 1.3, flexGrow: 1 }}>
                  {tr(stream === 'MAIN' ? 'gate.v1.lot.good' : 'gate.v1.lot.red')}
                </span>
                {ok ? (
                  <b style={{ fontSize: 20 }}>
                    {tr('gate.v1.perKg', { price: price(c.price_paise_per_kg!) })}
                  </b>
                ) : (
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.bad }}>
                    {tr('gate.v1.open.hasDeductions')}
                  </span>
                )}
              </div>
            )
          })}
        </Card>
      ))}
    </V1Screen>
  )
}

/** F4.11 — दिन बंद करें: the day's lots and what is still to send. No hand counts in V1. */
export function V1CloseDay() {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const g = useGate()
  const sync = usePending()
  const [busy, setBusy] = useState(false)
  const s = g.app
  const rec = useMemo(() => (s ? reconcile(g.outbox, s.id) : null), [g.outbox, s])
  if (!g.ready) return <V1Screen />
  if (!s || !rec) return <Navigate to="/m/gate" replace />
  const open = g.drafts.filter((x) => x.data.session_id === s.id)

  const close = async () => {
    setBusy(true)
    try {
      await closeDay(s, actor.user_id, {
        tags_counted: null,
        seals_counted: null,
        lots_main: rec.lotsMain,
        lots_conv: rec.lotsConv,
        slip_range: null,
      })
      void sync.syncNow()
      navigate('/m/gate', { replace: true })
    } catch (e) {
      toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <V1Screen
      nav
      header={
        <V1Header
          onBack={() => navigate('/m/gate')}
          pending={sync.pending}
          icon="shutterdown"
          title={tr('gate.v1.closeDay.title')}
        />
      }
      footer={
        <Footer>
          {open.length > 1 ? (
            <WaitCta>{tr('gate.v1.closeDay.draftsOpen', { count: open.length })}</WaitCta>
          ) : open[0] ? (
            <WaitCta>
              {tr('gate.v1.closeDay.draftOpen', { name: open[0].data.farmer.name })}
            </WaitCta>
          ) : (
            <Cta
              label={tr('gate.v1.closeDay.title')}
              icon="shutterdown"
              busy={busy}
              onClick={() => void close()}
            />
          )}
        </Footer>
      }
    >
      <Card>
        <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
          {tr('gate.v1.closeDay.lots')}
        </span>
        {(
          [
            ['sack', C.ok, 'gate.v1.closeDay.goodLots', rec.lotsMain, rec.bagsMain, rec.netMainG],
            ['redtie', C.ink, 'gate.v1.closeDay.redLots', rec.lotsConv, rec.bagsConv, rec.netConvG],
          ] as const
        ).map(([icon, color, key, n, bags, net]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name={icon} size={30} style={{ color }} />
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
              <b style={{ fontSize: 18 }}>{tr(key, { count: n })}</b>
              <span style={{ fontSize: 16 }}>
                {tr('gate.v1.closeDay.lotLine', { bags, kg: kgWhole(net) })}
              </span>
            </span>
          </div>
        ))}
      </Card>
      <ItemRow
        padding="6px 10px"
        lead={<Icon name="clock" size={28} />}
        title={tr('gate.v1.closeDay.toSend')}
        line={tr('gate.v1.closeDay.pending', { count: sync.pending, photos: sync.mediaPending })}
        right={
          <div style={{ flex: '0 0 auto', display: 'flex' }}>
            <Btn icon="clock" onClick={() => void sync.syncNow()}>
              {tr('gate.v1.closeDay.sendNow')}
            </Btn>
          </div>
        }
      />
      <Strip icon="cloudup" iconBox iconSize={24}>
        {sync.pending === 0 && sync.mediaPending === 0 ? (
          <b>{tr('gate.v1.closeDay.allSentDone')}</b>
        ) : (
          <Rich text={tr('gate.v1.closeDay.keepNetwork')} />
        )}
      </Strip>
    </V1Screen>
  )
}

const PAY: Record<
  string,
  { key: 'paid' | 'sent' | 'waiting' | 'problem'; tone: 'ok' | 'wait' | 'bad' }
> = {
  PROCESSED: { key: 'paid', tone: 'ok' },
  SUBMITTING: { key: 'sent', tone: 'wait' },
  PROCESSING: { key: 'sent', tone: 'wait' },
  PENDING_APPROVAL: { key: 'waiting', tone: 'wait' },
  READY: { key: 'waiting', tone: 'wait' },
  FAILED: { key: 'problem', tone: 'bad' },
  REVERSED: { key: 'problem', tone: 'bad' },
  CANCELLED: { key: 'problem', tone: 'bad' },
}

function PayMark({ tone }: { tone: 'ok' | 'wait' | 'bad' }) {
  if (tone === 'ok') return <Tick />
  if (tone === 'bad')
    return (
      <span
        aria-hidden="true"
        style={{
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: 4,
          background: C.bad,
          color: '#ffffff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="x" size={13} sw={3} />
      </span>
    )
  return (
    <span
      aria-hidden="true"
      style={{
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: 11,
        background: C.sunk,
        border: `1.5px solid ${C.border}`,
        color: C.ink,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Icon name="hourglass" size={13} />
    </span>
  )
}

/**
 * F4.9 — पैसा आया?: each purchase on this phone and where its pay is (per lot). The phone keeps
 * no money after sync (R10), so the lots show status, not rupees.
 */
export function V1PayStatus() {
  const navigate = useNavigate()
  const g = useGate()
  const sync = usePending()
  const [no, setNo] = useState('')
  // The board shows the list under a search box; the keypad opens only when she taps it.
  const [typing, setTyping] = useState(false)
  const onKey = useCallback((k: string) => setNo((c) => typeKey(c, k, { decimals: 0, max: 6 })), [])
  const rows = useMemo(() => {
    const out: (DayDelivery & { name: string; roster_no: string; paper: boolean })[] = []
    for (const s of g.sessions) {
      for (const x of deliveriesOf(g.outbox, s.id)) {
        if (x.kind !== 'COMMITTED') continue
        const f = g.roster?.farmers.find((y) => y.user_id === x.farmer_id)
        out.push({
          ...x,
          name: f?.name ?? '—',
          roster_no: f?.roster_no ?? '',
          paper: s.capture_mode === 'PAPER',
        })
      }
    }
    return out.sort((a, b) => (a.at < b.at ? 1 : -1))
  }, [g.sessions, g.outbox, g.roster])
  const shown = no ? rows.filter((r) => rosterNoMatches(r.roster_no, no)) : rows

  return (
    <V1Screen
      nav
      main={{ gap: 8 }}
      header={
        <V1Header
          onBack={() => navigate('/m/gate')}
          pending={sync.pending}
          icon="passbook"
          title={tr('gate.v1.pay.title')}
        />
      }
      footer={
        <Footer>
          <Cta
            label={tr('gate.v1.pay.home')}
            icon="home"
            lead
            onClick={() => navigate('/m/gate')}
          />
        </Footer>
      }
    >
      <button
        type="button"
        aria-expanded={typing}
        onClick={() => setTyping((t) => !t)}
        style={{
          background: C.surface,
          border: `2px solid ${typing ? C.acc : C.border}`,
          borderRadius: 12,
          padding: '6px 12px 8px',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
          color: C.ink,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, color: C.muted, fontWeight: 600, lineHeight: 1.3 }}>
            {tr('gate.v1.pay.search')}
          </div>
          <div style={{ minHeight: 28, fontSize: 26, fontWeight: 700, lineHeight: 1.05 }}>{no}</div>
        </div>
        <span style={{ color: C.muted, display: 'flex', alignSelf: 'center' }}>
          <Icon name="search" size={28} />
        </span>
      </button>
      {shown.length === 0 && <Strip icon="passbook">{tr('gate.v1.pay.none')}</Strip>}
      {shown.slice(0, 30).map((x) => (
        <section
          key={x.command_id}
          style={{
            background: C.surface,
            border: `1.5px solid ${C.rule}`,
            borderRadius: 12,
            padding: '6px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar size={40} icon={34} />
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minWidth: 0,
                lineHeight: 1.2,
              }}
            >
              <b style={{ fontSize: 17 }}>
                {x.name} · {tr('gate.v1.farmerNo', { no: rosterShort(x.roster_no) })}
              </b>
              <span
                style={{
                  fontSize: 14,
                  color: C.muted,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {fmtDate(x.at)}
                {x.paper && <Icon name="book" size={16} style={{ color: C.accDark }} />}
              </span>
            </span>
          </div>
          {x.lots.map((l) => {
            const p = l.payout_status ? PAY[l.payout_status] : undefined
            const tone = p?.tone ?? 'wait'
            const isRed = l.stream === 'CONV'
            return (
              <div key={l.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <PayMark tone={tone} />
                <span
                  aria-label={tr(isRed ? 'gate.v1.lot.red' : 'gate.v1.lot.good')}
                  style={{ color: isRed ? C.ink : C.ok, display: 'flex', flexShrink: 0 }}
                >
                  <Icon name={isRed ? 'redtie' : 'sack'} size={22} />
                </span>
                <span style={{ fontSize: 16, lineHeight: 1.3 }}>
                  <b>{tr('gate.v1.kg', { kg: kg(l.net_g) })}</b> ·{' '}
                  {p
                    ? tr(`gate.v1.pay.${p.key}`)
                    : x.sync === 'PENDING' || x.sync === 'SENDING'
                      ? tr('gate.v1.pay.notSent')
                      : tr('gate.v1.pay.waiting')}
                </span>
              </div>
            )
          })}
        </section>
      ))}
      {typing && (
        <>
          <div style={{ flexGrow: 1 }} />
          <Keypad dot={false} onKey={onKey} />
        </>
      )}
    </V1Screen>
  )
}
